import React, {
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  Box,
  Divider,
  Flex,
  FormControl,
  IconButton,
  Input,
  Switch,
  Text,
  VStack,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { weekDays } from 'utils/StaticData';
import PropTypes from 'prop-types';
import { validateSchedule } from 'utils/openingHoursValidation';

const OperatingHours = forwardRef(function OperatingHours(
  {
    formSchedule,
    errors: propErrors,
    handleSwitchChange,
    handleTimeChange,
    handleAddPeriod,
    handleRemovePeriod,
    handleDateChange, // New prop for date changes
    handleReasonChange, // New prop for reason changes
    handleRemoveDateSlot, // New prop for removing a date slot
    validateOnChange = true,
    onValidationChange,
    isTemporaryClosure = false,
  },
  ref,
) {
  const [showErrors, setShowErrors] = useState(false);

  // Use formSchedule directly (parent handles transformation)
  const displaySchedule = formSchedule;

  // Validate the schedule when it changes
  const validation = useMemo(() => {
    const result = validateSchedule(formSchedule);

    if (onValidationChange) {
      onValidationChange(!result.isValid);
    }

    return result;
  }, [formSchedule, onValidationChange]);

  // Combine prop errors with validation errors
  const errors = useMemo(() => {
    if (!validateOnChange) return propErrors;

    const combined = { ...propErrors };

    if (!validation.isValid) {
      combined.weeklySchedule = {
        ...(combined.weeklySchedule || {}),
        ...(validation.errors.weeklySchedule || {}),
      };
    }

    return combined;
  }, [propErrors, validation, validateOnChange]);

  // Helper to get error message for a day
  const getDayError = (dayIndex) => {
    if (!showErrors) return null;
    const dayErrors = errors.weeklySchedule?.[dayIndex];
    if (!dayErrors) return null;

    if (dayErrors._errors?.some((e) => e.type === 'DUPLICATE_DATE')) {
      return 'Duplicate date. Please select a different date.';
    }

    if (dayErrors._errors?.some((e) => e.type === 'MISSING_DATE')) {
      return 'Date is required for holiday hours.';
    }

    const periodErrors = dayErrors.periods;
    if (!periodErrors) return null;

    const hasAnyError = periodErrors.some(
      (period) => period?._errors?.length > 0,
    );
    if (!hasAnyError) return null;

    const hasOverlap = periodErrors.some((period) =>
      period?._errors?.some((e) => e.type === 'OVERLAPPING_PERIODS'),
    );

    return hasOverlap
      ? 'Time periods cannot overlap'
      : 'Please enter valid time periods';
  };

  // Helper to check if a specific input has an error
  const hasError = (dayIndex, periodIndex) => {
    const periodErrors =
      errors.weeklySchedule?.[dayIndex]?.periods?.[periodIndex];
    return periodErrors?._errors?.length > 0 || false;
  };

  // Expose validate method to parent
  useImperativeHandle(
    ref,
    () => ({
      validate: () => {
        const result = validateSchedule(formSchedule);
        const hasError = !result.isValid;
        setShowErrors(hasError);
        return hasError;
      },
    }),
    [formSchedule],
  );

  return (
    <Box
      mt={8}
      p={{ base: 0, md: 6 }}
      bg="white"
      borderRadius={{ base: 'none', md: 'lg' }}
      boxShadow="md"
      border={{ base: 'none', md: '1px solid #E2E8F0' }}
    >
      <Flex justify="center" width="100%">
        <Box maxW="800px" width="100%">
          <VStack
            spacing={5}
            alignItems="stretch"
            divider={<Divider borderColor="gray.200" />}
          >
            {displaySchedule?.map((day, dayIndex) => (
              <Box key={dayIndex}>
                <FormControl>
                  {/* Grid Layout: Day Name | Toggle | Time Slots */}
                  <Grid
                    templateColumns={
                      isTemporaryClosure
                        ? '150px 80px 1fr 200px 50px'
                        : '150px 80px 1fr'
                    }
                    gap={4}
                    alignItems="start"
                    width="100%"
                  >
                    {/* Column 1: Day Name or Date Input */}
                    <GridItem>
                      {isTemporaryClosure ? (
                        <FormControl
                          isInvalid={
                            !!errors.weeklySchedule?.[dayIndex]?._errors?.some(
                              (e) => e.type === 'MISSING_DATE',
                            )
                          }
                        >
                          <Input
                            type="date"
                            value={day.date}
                            onChange={(e) =>
                              handleDateChange(dayIndex, 'date', e.target.value)
                            }
                            width="150px"
                          />
                        </FormControl>
                      ) : (
                        <Text
                          fontWeight="semibold"
                          fontSize="md"
                          color="gray.700"
                          pt={2}
                        >
                          {weekDays[dayIndex]}
                        </Text>
                      )}
                    </GridItem>

                    {/* Column 2: Toggle Switch */}
                    <GridItem>
                      <Switch
                        pt={2}
                        isChecked={day.isOpen}
                        onChange={(e) =>
                          handleSwitchChange(
                            dayIndex,
                            'isOpen',
                            e.target.checked,
                          )
                        }
                        colorScheme="teal"
                        size="lg"
                        sx={{
                          'span.chakra-switch__track[data-checked]': {
                            backgroundColor: '#203946',
                          },
                        }}
                      />
                    </GridItem>

                    {/* Column 3: Time Slots */}
                    <GridItem>
                      {day.isOpen ? (
                        <VStack spacing={3} align="stretch">
                          {day.periods?.map((period, periodIndex) => (
                            <Flex key={periodIndex} align="center" gap={2}>
                              {/* Opening Time */}
                              <FormControl
                                isInvalid={
                                  !!errors.weeklySchedule?.[dayIndex]
                                    ?.periods?.[periodIndex]?.openingTime ||
                                  !!errors.weeklySchedule?.[dayIndex]
                                    ?.periods?.[periodIndex]?._errors?.length
                                }
                                maxW="120px"
                              >
                                <Input
                                  type="time"
                                  value={period.openingTime}
                                  onChange={(e) => {
                                    handleTimeChange(
                                      dayIndex,
                                      periodIndex,
                                      'openingTime',
                                      e.target.value,
                                    );
                                  }}
                                  size="sm"
                                  borderColor={
                                    hasError(dayIndex, periodIndex)
                                      ? 'red.500'
                                      : 'gray.200'
                                  }
                                  variant="outline"
                                  _focus={{
                                    borderColor: 'primary',
                                    boxShadow: '0 0 0 1px #203946',
                                  }}
                                  _hover={{
                                    borderColor: hasError(dayIndex, periodIndex)
                                      ? 'red.500'
                                      : 'gray.400',
                                  }}
                                  bg="white"
                                  borderRadius="6px"
                                  fontSize="sm"
                                  fontWeight="medium"
                                />
                              </FormControl>

                              {/* "to" text */}
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="medium"
                                minW="20px"
                                textAlign="center"
                              >
                                to
                              </Text>

                              {/* Closing Time */}
                              <FormControl
                                isInvalid={
                                  !!errors.weeklySchedule?.[dayIndex]
                                    ?.periods?.[periodIndex]?.closingTime ||
                                  !!errors.weeklySchedule?.[dayIndex]
                                    ?.periods?.[periodIndex]?._errors?.length
                                }
                                maxW="120px"
                              >
                                <Input
                                  type="time"
                                  value={period.closingTime}
                                  onChange={(e) => {
                                    handleTimeChange(
                                      dayIndex,
                                      periodIndex,
                                      'closingTime',
                                      e.target.value,
                                    );
                                  }}
                                  size="sm"
                                  borderColor={
                                    hasError(dayIndex, periodIndex)
                                      ? 'red.500'
                                      : 'gray.200'
                                  }
                                  variant="outline"
                                  _focus={{
                                    borderColor: 'primary',
                                    boxShadow: '0 0 0 1px #203946',
                                  }}
                                  _hover={{
                                    borderColor: hasError(dayIndex, periodIndex)
                                      ? 'red.500'
                                      : 'gray.400',
                                  }}
                                  bg="white"
                                  borderRadius="6px"
                                  fontSize="sm"
                                  fontWeight="medium"
                                />
                              </FormControl>

                              {/* Conditional Button Logic:
                              - If index 0 AND slots < 3: Show Add button
                              - If index 0 AND slots === 3: Show Delete button
                              - If index > 0: Show Delete button
                          */}
                              {periodIndex === 0 && day.periods?.length < 3 ? (
                                // First slot with less than 3 periods: Show Add button
                                <IconButton
                                  aria-label="Add period"
                                  icon={<AddIcon />}
                                  size="sm"
                                  bg="gray.100"
                                  color="gray.600"
                                  _hover={{ bg: 'gray.200' }}
                                  onClick={() => handleAddPeriod(dayIndex)}
                                  minW="32px"
                                  h="32px"
                                  borderRadius="6px"
                                />
                              ) : (
                                // All other cases: Show Delete button
                                day.periods?.length > 1 && (
                                  <IconButton
                                    aria-label="Delete period"
                                    icon={<DeleteIcon />}
                                    size="sm"
                                    bg="red.50"
                                    color="red.500"
                                    _hover={{ bg: 'red.100' }}
                                    onClick={() =>
                                      handleRemovePeriod(dayIndex, periodIndex)
                                    }
                                    minW="32px"
                                    h="32px"
                                    borderRadius="6px"
                                  />
                                )
                              )}
                            </Flex>
                          ))}
                        </VStack>
                      ) : (
                        <Flex align="center" minH="40px">
                          <Text
                            color="gray.500"
                            fontStyle="italic"
                            fontSize="md"
                            fontWeight="medium"
                          >
                            Closed
                          </Text>
                        </Flex>
                      )}
                      {/* Error Message */}
                      {getDayError(dayIndex) && (
                        <Box mt={2}>
                          <Text color="red.500" fontSize="sm">
                            {getDayError(dayIndex)}
                          </Text>
                        </Box>
                      )}
                    </GridItem>

                    {/* Column 4: Reason Input (Temporary Closures only) */}
                    {isTemporaryClosure && (
                      <GridItem>
                        <Input
                          placeholder="Reason"
                          value={day.reason}
                          onChange={(e) =>
                            handleReasonChange(
                              dayIndex,
                              'reason',
                              e.target.value,
                            )
                          }
                          width="200px"
                        />
                      </GridItem>
                    )}
                    {isTemporaryClosure && (
                      <GridItem>
                        <IconButton
                          aria-label="Delete date slot"
                          icon={<DeleteIcon />}
                          size="lg"
                          bg="red.50"
                          color="red.500"
                          _hover={{ bg: 'red.100' }}
                          onClick={() => handleRemoveDateSlot(dayIndex)}
                        />
                      </GridItem>
                    )}
                  </Grid>
                </FormControl>
              </Box>
            ))}
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
});

OperatingHours.propTypes = {
  formSchedule: PropTypes.arrayOf(
    PropTypes.shape({
      dayNumber: PropTypes.number,
      dayName: PropTypes.string,
      isOpen: PropTypes.bool.isRequired,
      periods: PropTypes.arrayOf(
        PropTypes.shape({
          openingTime: PropTypes.string.isRequired,
          closingTime: PropTypes.string.isRequired,
        }),
      ).isRequired,
      date: PropTypes.string,
      reason: PropTypes.string,
    }),
  ).isRequired,
  errors: PropTypes.object,
  handleSwitchChange: PropTypes.func.isRequired,
  handleTimeChange: PropTypes.func.isRequired,
  handleAddPeriod: PropTypes.func.isRequired,
  handleRemovePeriod: PropTypes.func.isRequired,
  handleDateChange: PropTypes.func,
  handleReasonChange: PropTypes.func,
  handleRemoveDateSlot: PropTypes.func,
  validateOnChange: PropTypes.bool,
  onValidationChange: PropTypes.func,
  isTemporaryClosure: PropTypes.bool,
};

OperatingHours.defaultProps = {
  errors: {},
  validateOnChange: true,
};

export default OperatingHours;
